import { Link } from 'react-router';
import type { EquipmentForSaleItem } from '@/api/sales';
import { ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SaleItemsGridProps {
    items: EquipmentForSaleItem[];
    isLoading: boolean;
}

const getConditionColor = (condition: string): string => {
    switch (condition?.toUpperCase()) {
        case 'A':
            return 'bg-success-subtle text-success';
        case 'B':
            return 'bg-blue-100 text-blue-800';
        case 'C':
            return 'bg-yellow-100 text-yellow-800';
        case 'D':
            return 'bg-orange-100 text-orange-800';
        default:
            return 'bg-muted text-foreground';
    }
};

export const SaleItemsGrid = ({ items, isLoading }: SaleItemsGridProps) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex flex-col space-y-4 animate-pulse">
                        <div className="aspect-square rounded-lg bg-muted" />
                        <div className="h-4 w-2/3 rounded bg-muted" />
                        <div className="h-4 w-1/3 rounded bg-muted" />
                    </div>
                ))}
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="flex h-64 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border p-12 text-center">
                <ShoppingCart className="mx-auto h-12 w-12 text-muted-foreground/70 mb-4" />
                <h3 className="mt-2 text-sm font-semibold text-foreground">No pre-loved gear available right now</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                    There are no pre-loved baby gear items available to buy at this location right now.
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8">
            {items.map((item) => (
                <Link
                    key={item.id}
                    to={`/equipment-for-sale/${item.id}`}
                    className="group block"
                >
                    <div className="rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden border border-border">
                        <div className="aspect-square bg-muted overflow-hidden">
                            {item.primaryImageUrl ? (
                                <img
                                    src={item.primaryImageUrl}
                                    alt={item.equipmentTypeName}
                                    className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-200"
                                    loading="lazy"
                                />
                            ) : (
                                <div className="h-full w-full flex items-center justify-center">
                                    <ShoppingCart className="h-16 w-16 text-muted-foreground/50" />
                                </div>
                            )}
                        </div>
                        <div className="p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <h3 className="text-sm font-medium text-foreground line-clamp-2">
                                    {item.equipmentTypeName}
                                    {item.brand && ` - ${item.brand}`}
                                </h3>
                                <span
                                    className={`shrink-0 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${getConditionColor(item.saleCondition)}`}
                                >
                                    {item.saleCondition}
                                </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                                {item.conditionDescription}
                            </p>
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-lg font-bold text-foreground">
                                        ${item.salePrice.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {item.locationName}
                                    </p>
                                </div>
                                <Button size="sm" variant="outline">
                                    View
                                </Button>
                            </div>
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
};
